/**
 * Installs a global fetch wrapper that on 401 tries to refresh the JWT;
 * if refresh fails or no refresh token, clears storage and dispatches auth:logout.
 */
import { API_BASE_URL } from './apiConfig';

const AUTH_LOGOUT_ITEMS = [
  'authToken',
  'user',
  'refreshToken',
  'rememberMe',
  'rememberedEmail',
  'sessionExpiration',
  'loginTime',
];

function clearAuthStorage(): void {
  AUTH_LOGOUT_ITEMS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  });
  window.dispatchEvent(new CustomEvent('auth:logout'));
}

export function installAuthFetch(): void {
  const originalFetch = window.fetch;
  window.fetch = async function authFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input);
    let response = await originalFetch.call(this, input, init);

    if (response.status === 401) {
      const isLogin = url.includes('/api/auth/login');
      const isRefresh = url.includes('/api/auth/refresh');
      if (isLogin || isRefresh) {
        return response;
      }

      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const refreshRes = await originalFetch(`${API_BASE_URL}/api/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          });
          if (refreshRes.ok) {
            const data = await refreshRes.json();
            if (data.success && data.data?.token) {
              localStorage.setItem('authToken', data.data.token);
              if (data.data.refreshToken) {
                localStorage.setItem('refreshToken', data.data.refreshToken);
              }
              if (data.data.user) {
                localStorage.setItem('user', JSON.stringify(data.data.user));
              }
              const newHeaders = new Headers(init?.headers);
              newHeaders.set('Authorization', `Bearer ${data.data.token}`);
              const newInit = { ...init, headers: newHeaders };
              return originalFetch.call(this, input, newInit);
            }
          }
        } catch {
          // refresh failed
        }
      }

      clearAuthStorage();
    }
    return response;
  };
}
